import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePublicAdmin } from "@/contexts/PublicAdminAuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, Trash2, User, GraduationCap, Calendar, ChevronRight } from "lucide-react";

const STREAM_LABELS = {
  STATE_QUOTA: "State Quota", AIIMS: "AIIMS", AIQ_15: "AIQ 15%",
  DEEMED: "Deemed", OTHER_STATES: "Other States",
};
const STREAM_COLORS = {
  STATE_QUOTA: { bg: "rgba(56,189,248,0.12)", border: "rgba(56,189,248,0.3)", text: "#38bdf8" },
  AIIMS:       { bg: "rgba(168,85,247,0.12)", border: "rgba(168,85,247,0.3)", text: "#a855f7" },
  AIQ_15:      { bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.3)", text: "#10b981" },
  DEEMED:      { bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.3)", text: "#f59e0b" },
  OTHER_STATES:{ bg: "rgba(239,68,68,0.12)",  border: "rgba(239,68,68,0.3)",  text: "#ef4444" },
};

export default function MedicalCounsellorStudents() {
  const { token } = usePublicAdmin();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState(null);

  const authHdr = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/public-admin/medical-students?search=${encodeURIComponent(search)}`, authHdr());
      setStudents(res.data);
    } catch { toast.error("Failed to load students"); }
    finally { setLoading(false); }
  }, [search, authHdr]);

  useEffect(() => { const t = setTimeout(fetchStudents, 300); return () => clearTimeout(t); }, [fetchStudents]);

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete profile for ${s.reg_number}?`)) return;
    setDeleting(s.id);
    try {
      await api.delete(`/public-admin/medical-students/${s.id}`, authHdr());
      toast.success("Student deleted"); fetchStudents();
    } catch { toast.error("Failed to delete"); }
    finally { setDeleting(null); }
  };

  const formatDate = (dt) => !dt ? "—" : new Date(dt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div style={{ padding: "1.5rem", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ marginBottom: "1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: "1.4rem", fontWeight: 800, color: "#f8fafc", margin: 0 }}>🏥 Medical Counsellor</h1>
          <p style={{ fontSize: "0.8rem", color: "#64748b", marginTop: 4 }}>{students.length} student profile{students.length !== 1 ? "s" : ""}</p>
        </div>
        <button onClick={() => navigate("/portal-admin/predictor")} style={{ display: "flex", alignItems: "center", gap: 8, background: "linear-gradient(135deg, #38bdf8, #818cf8)", border: "none", color: "#fff", padding: "9px 18px", borderRadius: 8, fontSize: "0.8rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          <Plus size={15} /> New Prediction
        </button>
      </div>
      <div style={{ position: "relative", marginBottom: "1.25rem" }}>
        <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#64748b" }} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by reg. number, name, or category..." style={{ width: "100%", padding: "10px 12px 10px 36px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, color: "#f8fafc", fontSize: "0.82rem", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
      </div>
      {loading ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "#64748b" }}>Loading…</div>
      ) : students.length === 0 ? (
        <div style={{ textAlign: "center", padding: "3rem", background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.08)", borderRadius: 14 }}>
          <GraduationCap size={40} style={{ opacity: 0.3, marginBottom: 12, color: "#64748b" }} />
          <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "#64748b" }}>No student profiles yet</div>
          <div style={{ fontSize: "0.75rem", marginTop: 6, color: "#475569" }}>Run a prediction and click <strong style={{ color: "#38bdf8" }}>Start Suggestion List</strong> to create one.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 0.8fr 0.6fr 0.5fr 70px", padding: "6px 16px", color: "#475569", fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px" }}>
            <span>Student</span><span>Ranks</span><span>Stream / Course</span><span>Category</span><span>List</span><span>Created</span><span></span>
          </div>
          {students.map(s => {
            const sc = STREAM_COLORS[s.stream] || STREAM_COLORS["STATE_QUOTA"];
            return (
              <div key={s.id} onClick={() => navigate(`/portal-admin/medical-counsellor/${s.id}`)}
                style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 0.8fr 0.6fr 0.5fr 70px", padding: "12px 16px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, cursor: "pointer", transition: "all 0.15s", alignItems: "center", borderLeft: "3px solid rgba(56,189,248,0.3)" }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(56,189,248,0.05)"; e.currentTarget.style.borderLeftColor = "#38bdf8"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; e.currentTarget.style.borderLeftColor = "rgba(56,189,248,0.3)"; }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(56,189,248,0.15)", border: "1px solid rgba(56,189,248,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><User size={14} style={{ color: "#38bdf8" }} /></div>
                  <div>
                    <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#f8fafc" }}>{s.reg_number}</div>
                    {s.student_name && <div style={{ fontSize: "0.62rem", color: "#64748b", marginTop: 1 }}>{s.student_name}</div>}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {s.air && <span style={{ fontSize: "0.7rem", color: "#38bdf8" }}>AIR: <strong>{s.air.toLocaleString()}</strong></span>}
                  {s.sml && <span style={{ fontSize: "0.7rem", color: "#a78bfa" }}>SML: <strong>{s.sml.toLocaleString()}</strong></span>}
                  {s.category_rank && <span style={{ fontSize: "0.7rem", color: "#34d399" }}>CAT: <strong>{s.category_rank.toLocaleString()}</strong></span>}
                  {!s.air && !s.sml && !s.category_rank && <span style={{ fontSize: "0.68rem", color: "#475569" }}>—</span>}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <span style={{ fontSize: "0.62rem", padding: "2px 7px", borderRadius: 4, background: sc.bg, border: `1px solid ${sc.border}`, color: sc.text, fontWeight: 700, display: "inline-block", width: "fit-content" }}>{STREAM_LABELS[s.stream] || s.stream || "—"}</span>
                  {s.course && <span style={{ fontSize: "0.65rem", color: "#94a3b8" }}>{s.course}</span>}
                </div>
                <div>
                  <span style={{ fontSize: "0.72rem", color: "#cbd5e1", fontWeight: 600 }}>{s.category || "—"}</span>
                  {s.gender && <div style={{ fontSize: "0.62rem", color: "#64748b" }}>{s.gender}</div>}
                </div>
                <div>
                  {s.suggestion_list?.length > 0 ? (
                    <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#10b981", background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)", padding: "2px 8px", borderRadius: 12 }}>{s.suggestion_list.length} items</span>
                  ) : <span style={{ fontSize: "0.68rem", color: "#475569" }}>Empty</span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Calendar size={11} style={{ color: "#475569" }} />
                  <span style={{ fontSize: "0.65rem", color: "#64748b" }}>{formatDate(s.created_at)}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                  <button onClick={e => { e.stopPropagation(); handleDelete(s); }} disabled={deleting === s.id} style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#f87171", padding: "5px 7px", borderRadius: 6, cursor: "pointer", lineHeight: 0 }} title="Delete">
                    <Trash2 size={12} />
                  </button>
                  <ChevronRight size={15} style={{ color: "#38bdf8" }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
